import { useState, useEffect, useCallback } from 'react';
import { cacheManager } from '@/utils/cache/cacheManager';
import { handleError, ErrorType, AppError } from '@/utils/error/errorHandler';

export interface UseDataOptions<T> {
  // Function that fetches the data
  fetchFn: () => Promise<T>;
  
  // Cache key to use for this data
  cacheKey?: string;
  
  // Time to live for the cache in milliseconds
  cacheTTL?: number;
  
  // Whether to fetch data immediately on hook mount
  fetchOnMount?: boolean;
  
  // Dependencies array for refetching (similar to useEffect dependencies)
  dependencies?: any[];
  
  // Optional callback to run when data is successfully fetched
  onSuccess?: (data: T) => void;
  
  // Optional callback to run when fetching fails
  onError?: (error: AppError) => void;
}

export interface UseDataResult<T> {
  // The fetched data
  data: T | null;
  
  // Whether data is currently being loaded
  isLoading: boolean;
  
  // Error object if the fetch failed
  error: AppError | null;
  
  // Function to manually trigger a refetch
  refetch: () => Promise<void>;
  
  // Function to manually update the data (useful for optimistic updates)
  setData: (newData: T | ((prevData: T | null) => T)) => void;
  
  // Function to manually clear the error
  clearError: () => void;
}

/**
 * A generic hook for data fetching with built-in loading state, error handling, and caching
 * 
 * @param options - Configuration options for the hook
 * @returns Object containing data, loading state, error, and utility functions
 */
export function useData<T>(options: UseDataOptions<T>): UseDataResult<T> {
  const {
    fetchFn,
    cacheKey,
    cacheTTL,
    fetchOnMount = true,
    dependencies = [],
    onSuccess,
    onError
  } = options;

  const [data, setData] = useState<T | null>(
    // Initialize from cache if available
    cacheKey ? cacheManager.get<T>(cacheKey) : null
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<AppError | null>(null);

  // Function to fetch data with caching and error handling
  const fetchData = useCallback(async (): Promise<void> => {
    // Skip if already loading
    if (isLoading) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      let result: T;
      
      // Use cache if available and requested
      if (cacheKey && cacheManager.has(cacheKey)) {
        const cachedData = cacheManager.get<T>(cacheKey);
        if (cachedData !== null) {
          setData(cachedData);
          setIsLoading(false);
          onSuccess?.(cachedData);
          return;
        }
      }
      
      // Fetch fresh data
      result = await fetchFn();
      
      // Cache the result if caching is enabled
      if (cacheKey) {
        cacheManager.set(cacheKey, result, cacheTTL);
      }
      
      setData(result);
      onSuccess?.(result);
    } catch (err) {
      const appError = handleError(err, 'DATA_FETCH');
      setError(appError);
      onError?.(appError);
    } finally {
      setIsLoading(false);
    }
  }, [fetchFn, cacheKey, cacheTTL, isLoading, onSuccess, onError]);

  // Function to manually update data and cache
  const updateData = useCallback((newData: T | ((prevData: T | null) => T)) => {
    const updatedData = typeof newData === 'function' 
      ? (newData as Function)(data) 
      : newData;
    
    setData(updatedData);
    
    // Update cache if caching is enabled
    if (cacheKey && updatedData !== null) {
      cacheManager.set(cacheKey, updatedData, cacheTTL);
    }
  }, [data, cacheKey, cacheTTL]);

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Fetch data on mount and when dependencies change
  useEffect(() => {
    if (fetchOnMount) {
      fetchData();
    }
  }, [...dependencies, fetchData]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchData,
    setData: updateData,
    clearError
  };
} 